import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CoordinateInputComponent } from '@wheere-front/shared-ui';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumber } from 'primeng/inputnumber';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';

export type SitePositionDialogMode = 'gateway' | 'tracker' | 'antenna';

export interface SitePositionResult {
  lat: number;
  lng: number;
  altitude: number;
  antennaHeight?: number;
}

/**
 * Coordinate-input modal used by the site Configuration tab in the integrator. Drop-in
 * replacement for the inside `lib-position-dialog`. Same look-and-feel: lat/lng row +
 * altitude row, plus an antenna height row when the mode is `tracker`.
 */
@Component({
  selector: 'lib-site-position-dialog',
  standalone: true,
  imports: [DialogModule, ReactiveFormsModule, CoordinateInputComponent, InputNumber, ButtonModule, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      [header]="dialogTitle()"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '95vw', maxWidth: '750px' }"
      [breakpoints]="{ '640px': '100vw' }"
      (onHide)="onCancel()">
      <form [formGroup]="form" class="flex flex-col gap-3">
        <lib-coordinate-input
          #coordinateInput
          formControlName="position"
          [lat]="form.value.position?.lat"
          [lng]="form.value.position?.lng">
        </lib-coordinate-input>

        <div
          class="grid gap-3"
          [class.grid-cols-1]="mode() !== 'tracker'"
          [class.sm:grid-cols-2]="mode() === 'tracker'">
          <div>
            <label for="posAltitude" class="mb-1 block text-sm font-medium text-gray-600">
              {{
                mode() === 'tracker'
                  ? ('sitePositionDialog.fields.trackerAlt' | transloco)
                  : ('sitePositionDialog.fields.altitude' | transloco)
              }}
            </label>
            <p-input-number
              id="posAltitude"
              styleClass="w-full"
              formControlName="altitude"
              [placeholder]="'sitePositionDialog.fields.altitudePlaceholder' | transloco"
              mode="decimal"
              maxFractionDigits="5"
              [min]="-9000"
              [max]="9000">
            </p-input-number>
          </div>

          @if (mode() === 'tracker') {
            <div>
              <label for="antennaHeight" class="mb-1 block text-sm font-medium text-gray-600">{{
                'sitePositionDialog.fields.antennaAlt' | transloco
              }}</label>
              <p-input-number
                id="antennaHeight"
                styleClass="w-full"
                formControlName="antennaHeight"
                [placeholder]="'sitePositionDialog.fields.antennaAltPlaceholder' | transloco"
                mode="decimal"
                maxFractionDigits="5"
                [min]="-9000"
                [max]="9000">
              </p-input-number>
              @if (antennaDiffTooHigh) {
                <div class="mt-1.5 flex items-center gap-1.5 text-xs text-red-500">
                  <i class="pi pi-exclamation-triangle text-xs"></i>
                  {{ 'sitePositionDialog.errors.diffTooHigh' | transloco }}
                </div>
              }
            </div>
          }
        </div>
      </form>

      <ng-template #footer>
        <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <p-button
            [label]="'sitePositionDialog.actions.cancel' | transloco"
            icon="pi pi-times"
            severity="secondary"
            [outlined]="true"
            (click)="onCancel()"
            styleClass="w-full sm:w-auto">
          </p-button>
          <p-button
            [label]="'sitePositionDialog.actions.save' | transloco"
            icon="pi pi-check"
            (click)="onSave()"
            [disabled]="form.invalid"
            styleClass="w-full sm:w-auto">
          </p-button>
        </div>
      </ng-template>
    </p-dialog>
  `,
})
export class SitePositionDialogComponent {
  mode = input<SitePositionDialogMode>('gateway');
  visible = model<boolean>(false);

  /** Initial values — patched into the form whenever the dialog becomes visible. */
  initialLat = input<number | null>(null);
  initialLng = input<number | null>(null);
  initialAlt = input<number | null>(null);
  initialAntennaAlt = input<number | null>(null);

  positionSaved = output<SitePositionResult>();

  @ViewChild('coordinateInput') coordinateInput?: CoordinateInputComponent;

  form: FormGroup;

  #transloco = inject(TranslocoService);
  // Re-derive the translated dialog title whenever the active language changes.
  protected readonly activeLang = toSignal(this.#transloco.langChanges$, {
    initialValue: this.#transloco.getActiveLang(),
  });

  protected readonly dialogTitle = computed<string>(() => {
    // Touch the active lang signal so this re-runs on language change.
    this.activeLang();
    switch (this.mode()) {
      case 'gateway':
        return this.#transloco.translate('sitePositionDialog.title.gateway');
      case 'tracker':
        return this.#transloco.translate('sitePositionDialog.title.tracker');
      case 'antenna':
        return this.#transloco.translate('sitePositionDialog.title.antenna');
    }
  });

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      position: this.fb.control<{ lat: number; lng: number }>(
        { lat: undefined!, lng: undefined! },
        { validators: Validators.required }
      ),
      altitude: [undefined, Validators.required],
      antennaHeight: [undefined],
    });

    // Re-patch the form every time the dialog opens so the user sees the current values.
    effect(() => {
      if (this.visible()) {
        const lat = this.initialLat();
        const lng = this.initialLng();
        const alt = this.initialAlt();
        const antAlt = this.initialAntennaAlt();
        // Defer to next tick so the form controls are connected first.
        queueMicrotask(() => {
          this.form.patchValue({
            position: { lat: lat as number, lng: lng as number },
            altitude: alt ?? undefined,
            antennaHeight: antAlt ?? undefined,
          });
        });
      }
    });
  }

  get antennaDiffTooHigh(): boolean {
    const altitude = this.form.get('altitude')?.value;
    const antennaHeight = this.form.get('antennaHeight')?.value;
    return (
      altitude != null &&
      antennaHeight != null &&
      !isNaN(altitude) &&
      !isNaN(antennaHeight) &&
      antennaHeight - altitude > 2
    );
  }

  patchValues(
    lat: number | null | undefined,
    lng: number | null | undefined,
    altitude: number | null | undefined,
    antennaHeight?: number | null | undefined
  ): void {
    this.form.patchValue({
      position: { lat: lat as number, lng: lng as number },
      altitude: altitude ?? undefined,
      antennaHeight: antennaHeight ?? undefined,
    });
  }

  onSave(): void {
    if (this.form.invalid) return;

    let coordinates: { lat: number; lng: number };
    if (this.coordinateInput) {
      coordinates = this.coordinateInput.forceCalculateAndGetValue();
    } else {
      coordinates = this.form.value.position;
    }

    const result: SitePositionResult = {
      lat: coordinates.lat,
      lng: coordinates.lng,
      altitude: this.form.value.altitude,
    };

    if (this.mode() === 'tracker') {
      result.antennaHeight = this.form.value.antennaHeight;
    }

    this.positionSaved.emit(result);
    this.visible.set(false);
    this.form.reset();
  }

  onCancel(): void {
    this.visible.set(false);
    this.form.reset();
  }
}
